import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { addDays } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import APP from '../../components/paypal';
import { Icon } from '../../components/header/draft/Icon';
import { AmbientBackground } from '../../components/layout/AmbientBackground';
import { GlassSurface } from '../../components/profile/GlassSurface';
import '../../components/profile/profile-glass.css';
import AuthAxios from '../../axios';
import { AuthStateContext } from '../../contexts/AuthContext';
import { ModalContext } from '../../contexts/ModalContext';
import type { Product } from '../../types';
import { fetchRegisteredPaymentCard } from '../../utils/payment/paymentMethodApi';
import { transLn } from '../../utils/transLn';
import FailedPaymentModal from './PaymentFailedModal';
import './subscription-payment-page.css';

declare const window: typeof globalThis & {
  IMP: any;
};

export default function PaymentPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const product = location.state?.product as Product | undefined;
  const navigate = useNavigate();
  const { handleModal } = useContext(ModalContext);
  const { user, handleSubscription } = useContext(AuthStateContext);

  const [loading, setLoading] = useState(false);
  const [paypal, setPaypal] = useState(false);

  useEffect(() => {
    if (!product) {
      navigate('/app/membership', { replace: true });
      return;
    }

    fetchRegisteredPaymentCard().catch(() => {});
  }, [product, navigate]);

  const handlePostPayment = (rsp: unknown) => {
    setLoading(true);
    AuthAxios.post('/port-one/payment', rsp)
      .then(() => {
        handleSubscription();
        navigate('/app/home', { replace: true });
        handleModal(<SuccessPaymentModal />);
      })
      .catch(() => {
        handleModal(<FailedPaymentModal />);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onClickpostPaypalPayment = (selected: Product) => {
    const { IMP } = window;
    setPaypal(true);

    const now = new Date();
    const customerUID = uuidv4();
    const merchantUID = uuidv4();
    const from = now;
    const to = addDays(now, selected.duration);

    IMP.init(process.env.REACT_APP_IMP_CODE);
    IMP.loadUI(
      'paypal-rt',
      {
        pg: `paypal_v2.${process.env.REACT_APP_PAYPAL_MID}`,
        pay_method: 'paypal',
        amount: selected.price,
        customer_uid: customerUID,
        name: transLn(selected.description),
        merchant_uid: merchantUID,
        custom_data: {
          userId: user?.id,
          productId: selected.id,
          startDate: from,
          endDate: to,
        },
      },
      () => {
        navigate('/app/home', { replace: true });
      },
    );
  };

  const onClickpostPayment = () => {
    if (!product) return;

    const { IMP } = window;
    IMP.init(process.env.REACT_APP_IMP_CODE);

    const now = new Date();
    const customerUID = uuidv4();
    const from = now;
    const to = addDays(now, product.duration);
    const merchant_uid = uuidv4();

    IMP.request_pay(
      {
        pg: `danal_tpay.${process.env.REACT_APP_IMP_MID}`,
        pay_method: 'card',
        amount: product.price,
        customer_uid: customerUID,
        name: transLn(product.description),
        period: { from, to },
        merchant_uid,
        buyer_email: user?.email || '',
        buyer_name: user?.nickName || '',
        custom_data: {
          userId: user?.id,
          productId: product.id,
          startDate: from,
          endDate: to,
        },
      },
      (rsp: { success?: boolean }) => {
        if (rsp.success) {
          handlePostPayment({ ...rsp });
        } else {
          handleModal(<FailedPaymentModal reason="invalidCard" />);
        }
      },
    );
  };

  if (!product) {
    return null;
  }

  const benefits = t('settingsDraft.benefits', { returnObjects: true }) as string[];
  const productName = transLn(product.name);
  const productDesc = transLn(product.description);
  const displayName = productName.includes('!') ? productName.split('!')[0] : productName;
  const listPrice = product.price || 0;
  const totalPayment = listPrice;

  return (
    <div className="subscription-payment-page soundpill-profile-view">
      <AmbientBackground />
      <div className="subscription-payment-page__scroll custom-scrollbar">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="subscription-payment-page__back"
        >
          <Icon name="arrow_back" className="text-xl" />
          {t('paymentMembershipPage.back')}
        </button>

        <header className="subscription-payment-page__hero">
          <span className="subscription-payment-page__badge">
            <Icon name="verified" className="text-sm" filled />
            Premium
          </span>
          <h1 className="subscription-payment-page__title">
            <strong>SoundPill</strong> {t('paymentMembershipPage.becomePremiumMember')}
          </h1>
          <p className="subscription-payment-page__lead">{t('paymentMembershipPage.checkoutLead')}</p>
        </header>

        <GlassSurface className="subscription-payment-page__product-card w-full mb-4" rounded="2xl">
          <p className="subscription-payment-page__product-name">{displayName}</p>
          {productDesc ? (
            <p className="subscription-payment-page__product-desc">{productDesc}</p>
          ) : null}
          <p className="subscription-payment-page__product-price">₩{listPrice.toLocaleString()}</p>
          {product.priceUSD ? (
            <span className="subscription-payment-page__product-price-usd">
              ${product.priceUSD.toLocaleString()} / {t('paymentManagementPage.perMonth')}
            </span>
          ) : null}
        </GlassSurface>

        <GlassSurface className="px-5 py-4 mb-4 w-full" rounded="2xl">
          <span className="subscription-payment-page__panel-label">
            {t('paymentMembershipPage.accountWhereBenefitsApply')}
          </span>
          <p className="subscription-payment-page__account-email">{user?.email}</p>
        </GlassSurface>

        <GlassSurface className="px-5 py-4 mb-4 w-full" rounded="2xl">
          <span className="subscription-payment-page__panel-label">
            {t('paymentMembershipPage.premiumBenefits')}
          </span>
          <ul className="subscription-payment-page__benefit-list">
            {benefits.map((benefit) => (
              <li key={benefit} className="subscription-payment-page__benefit-item">
                <span className="subscription-payment-page__benefit-check">
                  <Icon name="check" className="text-xs" />
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </GlassSurface>

        <GlassSurface className="px-5 py-5 w-full" rounded="2xl">
          <span className="subscription-payment-page__panel-label">
            {t('paymentMembershipPage.orderSummary')}
          </span>
          <div className="subscription-payment-page__summary-row">
            <span className="subscription-payment-page__summary-name">{productDesc || displayName}</span>
            <span className="subscription-payment-page__summary-amount">₩{listPrice.toLocaleString()}</span>
          </div>
          <div className="subscription-payment-page__total">
            <strong>{t('paymentMembershipPage.totalAfterDiscount')}</strong>
            <span className="subscription-payment-page__total-amount">
              ₩{totalPayment.toLocaleString()}
            </span>
          </div>
        </GlassSurface>

        <div className="subscription-payment-page__actions">
          <button
            type="button"
            disabled={loading}
            onClick={onClickpostPayment}
            className="subscription-payment-page__btn subscription-payment-page__btn--primary"
          >
            {loading
              ? t('paymentMembershipPage.paymentProcessing')
              : t('paymentMembershipPage.payWithKoreanCard')}
          </button>
          <div className="portone-ui-container" data-portone-ui-type="paypal-rt" />
          {paypal ? <APP /> : null}
          <button
            type="button"
            disabled={loading}
            onClick={() => onClickpostPaypalPayment(product)}
            className="subscription-payment-page__btn subscription-payment-page__btn--secondary"
          >
            {t('paymentMembershipPage.payWithInternationalCard')}
          </button>
        </div>

        <ul className="subscription-payment-page__reassure">
          <li className="subscription-payment-page__reassure-title">
            {t('paymentMembershipPage.allTransactionsAreSecure')}
          </li>
          <li>{t('paymentMembershipPage.annualPremiumMembership')}</li>
          <li>{t('paymentMembershipPage.ifYouWishToCancelSubscription')}</li>
        </ul>
      </div>
    </div>
  );
}

function SuccessPaymentModal() {
  const { handleModal } = useContext(ModalContext);
  const { t } = useTranslation();

  return (
    <div className="subscription-payment-success-modal">
      <h2 className="subscription-payment-success-modal__title">
        {t('paymentMembershipPage.paymentSuccessTitle')}
      </h2>
      <p className="subscription-payment-success-modal__lead">
        {t('paymentMembershipPage.paymentSuccessSubtitle')}
      </p>
      <button
        type="button"
        className="subscription-payment-page__btn subscription-payment-page__btn--primary"
        onClick={() => handleModal()}
      >
        {t('paymentMembershipPage.confirm')}
      </button>
    </div>
  );
}
